import {getRoleChanged} from '../store/AuthSlice';
import {useAppSelector} from '../store/Store';
import impersonationData from '../components/Dashboard/impersonation.json';

const rolesMap: Record<string, string> = {
  SpendwiseUser: 'Manager User',
  'Manager User': 'Manager User',
  'Individual/Manager User': 'Manager User',
  'Individual / Manager User': 'Manager User',
  SuperUser: 'SuperUser',
  'Super User': 'SuperUser',
};

const getNormalizedRole = (role: string) => rolesMap?.[role] ?? role;

/**
 * Get impersonated email and GPID for a user
 * Returns object with email and gpid, or null if user is not in impersonation list
 */
const getImpersonatedDetails = (
  userEmail: string | undefined
): {email: string; gpid: string} | null => {
  if (!userEmail) return null;

  if (!Array.isArray(impersonationData) || impersonationData.length < 2) {
    return null;
  }

  const emailMapping = impersonationData[0] as unknown as Record<
    string,
    string
  >;
  const gpidMapping = impersonationData[1] as unknown as Record<string, string>;
  const lowerUserEmail = userEmail.toLowerCase();

  // Find impersonated email
  for (const key in emailMapping) {
    if (key.toLowerCase() === lowerUserEmail) {
      const impersonatedEmail = emailMapping[key];
      // Get GPID for the impersonated email
      const lowerImpersonatedEmail = impersonatedEmail.toLowerCase();
      for (const gpidKey in gpidMapping) {
        if (gpidKey.toLowerCase() === lowerImpersonatedEmail) {
          return {
            email: impersonatedEmail,
            gpid: gpidMapping[gpidKey],
          };
        }
      }
      return {
        email: impersonatedEmail,
        gpid: '',
      };
    }
  }

  return null;
};

const useRole = (role: string, userEmail?: string) => {
  const isManagerToggle = useAppSelector<boolean>(getRoleChanged);
  const impersonatedDetails = getImpersonatedDetails(userEmail);
  const isImpersonating = impersonatedDetails !== null;

  let currentRole = getNormalizedRole(role);
  let effectiveImpersonatedEmail: string | null = null;
  let effectiveImpersonatedGpid: string | null = null;

  if (isImpersonating) {
    currentRole = 'Manager User';

    if (!isManagerToggle) {
      effectiveImpersonatedEmail = impersonatedDetails!.email;
      effectiveImpersonatedGpid = impersonatedDetails!.gpid;
    }
  }

  const isManagerRole = currentRole === 'Manager User' || isManagerToggle;

  return {
    getNormalizedRole,
    isManagerRole,
    currentRole,
    impersonatedEmail: effectiveImpersonatedEmail,
    impersonatedGpid: effectiveImpersonatedGpid,
    isImpersonating,
  };
};

export default useRole;
